import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('click', this.#handleAddToCartClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#replayShippingProgress);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('click', this.#handleAddToCartClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#replayShippingProgress);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /** @param {MouseEvent} event */
  #handleAddToCartClick = (event) => {
    const btn = /** @type {HTMLButtonElement | null} */ (/** @type {Element} */ (event.target)?.closest?.('[ref="addToCartButton"]'));
    if (!btn || btn.disabled) return;

    const form = btn.closest('form');
    if (!form?.checkValidity()) return;

    btn.setAttribute('data-loading', 'true');
    const text = /** @type {HTMLElement | null} */ (btn.querySelector('.add-to-cart-text'));
    if (text) text.style.visibility = 'hidden';
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    document.querySelectorAll('[ref="addToCartButton"][data-loading="true"]').forEach((btn) => {
      btn.removeAttribute('data-loading');
      const text = /** @type {HTMLElement | null} */ (btn.querySelector('.add-to-cart-text'));
      if (text) text.style.visibility = '';
    });

    const shouldSuppressDrawerOpen = event.detail.data?.suppressDrawerOpen === true;
    if (this.hasAttribute('auto-open') && !shouldSuppressDrawerOpen) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Keep the compact order controls visible while the product list, followed
    // by the PWP carousel, moves through one continuous scroll area.
    dialog.setAttribute('cart-summary-sticky', 'true');
  }

  #replayShippingProgress = () => {
    const bar = /** @type {HTMLElement | null} */ (
      this.refs.dialog?.querySelector('.ruvine-shipping-progress__bar') ?? null
    );
    if (!bar) return;

    const vehicle = this.refs.dialog?.querySelector('.ruvine-shipping-progress__vehicle');
    bar.getAnimations().forEach((animation) => animation.cancel());
    vehicle?.getAnimations().forEach((animation) => animation.cancel());
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    vehicle?.animate([
      { transform: `translate(calc(-50% - ${bar.getBoundingClientRect().width}px), -50%)` },
      { transform: 'translate(-50%, -50%)' },
    ], { duration: 1200, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
    bar.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
      duration: 1200,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    });
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
